import Drawer from '@atoms/Drawer/Drawer';
import SelectorButton from '@atoms/SelectorButton/SelectorButton';
import { IProductVariant } from '@models/IProductVariant';
import ColourSwatch from '@molecules/ColourSwatch/ColourSwatch';
import ProductDetailsVariantCard from '@molecules/ProductDetailsVariantCard/ProductDetailsVariantCard';
import updateDataLayer from '@utils/helpers/gtm';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface Props {
  readonly colours: IProductVariant[];
  readonly coloursLabel: string;
  readonly variations: IProductVariant[];
  readonly variationsLabel: string;
  readonly productsTitle: string;
  readonly productsSubTitle: string;
  readonly skuCode: string;
  readonly primarySkuCode: string;
  readonly outOfStockLabel: string;
  readonly dataTestId?: string;
}

const viewLabel = (label: string, items: IProductVariant[]) =>
  label.replace('{0}', `${items?.length ?? 0}`);

const ProductDetailsOptions = ({
  colours,
  coloursLabel,
  variations,
  variationsLabel,
  productsTitle,
  productsSubTitle,
  skuCode,
  primarySkuCode,
  outOfStockLabel,
  dataTestId = 'Product-Details-Options',
}: Props) => {
  const [isOptionsDrawerOpen, setIsOptionsDrawerOpen] = useState(false);
  const currentVariant =
    variations?.find((item) => item.skuCode === skuCode) ??
    variations?.find((item) => item.skuCode === primarySkuCode);

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={dataTestId}
    >
      {colours?.length > 0 && (
        <div className="flex w-full flex-col gap-1.5 rounded-[10px] bg-grey-100 p-5 dark:bg-grey-600 md:gap-1 md:py-[18px] lg:py-4">
          {coloursLabel && (
            <span
              className="body-four text-grey-450 dark:text-grey-500"
              data-testid={`${dataTestId}-Colours-Label`}
            >
              {coloursLabel}
            </span>
          )}
          <ColourSwatch
            dataTestId={`${dataTestId}-Colour-Swatch`}
            variations={colours}
            currentSkuCode={skuCode}
          />
        </div>
      )}

      {variations?.length > 1 && (
        <SelectorButton
          title={viewLabel(variationsLabel, variations)}
          subTitle={currentVariant?.name}
          onClick={() => {
            updateDataLayer({
              event: 'productModification',
              content_type: 'bundle_and_save',
              selection: currentVariant?.skuCode,
            });
            setIsOptionsDrawerOpen(true);
          }}
          dataTestId={`${dataTestId}-Variant-Selector`}
        />
      )}

      <Drawer
        isOpen={isOptionsDrawerOpen}
        setIsOpen={setIsOptionsDrawerOpen}
        dataTestId={`${dataTestId}-Variant-Selector-Drawer`}
      >
        <div className="flex h-full flex-col text-black-100">
          <div className="flex grow flex-col overflow-hidden px-5 lg:px-16">
            {productsTitle && (
              <p
                className="h5 mb-2.5"
                data-testid={`${dataTestId}-Variant-Selector-Drawer-Title`}
              >
                {productsTitle}
              </p>
            )}
            {productsSubTitle && (
              <p
                className="body-two pb-10"
                data-testid={`${dataTestId}-Variant-Selector-Drawer-Subtitle`}
              >
                {productsSubTitle}
              </p>
            )}
            {variations?.length > 0 && (
              <div className="relative h-full overflow-y-hidden after:absolute after:inset-0 after:top-auto after:h-[100px] after:bg-gradient-to-t after:from-grey-100 after:content-['']">
                <div className="h-full overflow-y-auto">
                  <motion.div
                    className="grid grid-cols-2 items-start gap-4 pb-20"
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.5,
                        },
                      },
                    }}
                  >
                    {variations.map((variant, index) => (
                      <ProductDetailsVariantCard
                        key={variant.skuCode}
                        name={variant.name}
                        skuCode={variant.skuCode}
                        url={variant.url}
                        thumbnail={variant.thumbnail}
                        pricingData={variant.pricingData}
                        activeVariation={
                          variant.skuCode === currentVariant?.skuCode
                        }
                        outOfStockLabel={outOfStockLabel}
                        dataTestId={`${dataTestId}-Variant-Selector-Drawer-Variant-Card-${index}`}
                      />
                    ))}
                  </motion.div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </motion.div>
  );
};

export default ProductDetailsOptions;
